import { ref } from 'vue';
import { stringToDate, formatDateForApi } from '@/shared/lib';
import { getDocumentControl } from '@/entities/Document';
import { fetchAllMembersAsUsers } from '@/entities/User';
import { positionApi } from '@/entities/Position';
import editHomologationApi from '../api/editHomologation.api';
import type {
  DocumentRevision,
  DocumentControlResponse,
  DocumentControlTrainingData,
} from '@/entities/Document';
import {
  buildBranchMemberOptions,
  collectBranchMemberships,
  deduplicatePositions,
  getMemberPositionsFromUsers,
  mapNamedPositions,
} from '@/entities/User';
import type { CollaboratorLike, MemberOption, MembershipLike } from '@/entities/User';
import type { EditHomologationFormData, TrainingSessionUpdate } from './editHomologation.types';
import type { Position } from '@/entities/Position';


interface UserWithPositions {
  name: string;
  positions?: Array<{ name: string }>;
}


interface UserPositionContext {
  users: CollaboratorLike[];
  positions: Position[];
  branchId?: number;
}

type UserLookup = Map<number, CollaboratorLike>;

type DateInput = string | Date | null | undefined;

// Re-exported so the existing importers of this module keep working after the two member helpers
// moved to the User entity, where the record screen can reach them without importing a feature.
export { buildBranchMemberOptions, getMemberPositionsFromUsers };
export type { MemberOption };

function resolveHomologationBranchId(revision: DocumentRevision): number | undefined {
  const fromDoc = revision.document?.branchId ?? revision.document?.branch?.id;
  if (fromDoc !== undefined && fromDoc !== null) {
    const n = Number(fromDoc);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  if (typeof localStorage !== 'undefined') {
    const raw = localStorage.getItem('selectedBranchId');
    if (raw) {
      const n = Number.parseInt(raw, 10);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
    }
  }
  return undefined;
}

const normalizeName = (name: string): string => name.trim().toLowerCase();

const findPositionIdByName = (name: string, positions: Position[]): number | undefined => {
  if (!name || !name.trim()) return undefined;
  const normalized = normalizeName(name);
  const position = positions.find((p) => p.name && normalizeName(p.name) === normalized);
  if (!position && import.meta.env.DEV) {
    console.warn(`Cargo não encontrado: ${name}`);
  }
  return position?.id;
};

const extractErrorMessage = (err: unknown): string => {
  const defaultMessage = 'Erro ao salvar configurações. Tente novamente';

  if (!err || typeof err !== 'object' || !('response' in err)) {
    return defaultMessage;
  }

  const response = err.response as
    | { data?: { message?: string; errors?: Record<string, string[]> } }
    | null
    | undefined;

  if (!response) {
    return defaultMessage;
  }

  if (response.data?.message) {
    return response.data.message;
  }

  if (response.data?.errors) {
    const messages = Object.values(response.data.errors).flat().filter(Boolean);
    return messages.length > 0 ? messages.join('; ') : defaultMessage;
  }

  return defaultMessage;
};

const convertDateToISO = (dateValue: DateInput): string | undefined => {
  if (!dateValue) return undefined;
  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? undefined : dateValue.toISOString();
  }
  if (typeof dateValue === 'string' && dateValue.includes('T')) {
    const parsed = new Date(dateValue);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
  }
  // Civil-date fallback (DD/MM/YYYY or YYYY-MM-DD) — pin to UTC midnight so
  // the resulting ISO string is TZ-invariant. Going through stringToDate
  // (local midnight) + toISOString would land on different UTC instants in
  // different browser TZs.
  const date = stringToDate(dateValue);
  if (!date) return undefined;
  const utcMidnight = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  return utcMidnight.toISOString();
};

const convertDateToCivilString = (dateValue: DateInput): string | undefined => {
  if (!dateValue) return undefined;
  const date = stringToDate(dateValue);
  return date ? (formatDateForApi(date) ?? undefined) : undefined;
};

const normalizeTrainingDate = (dateValue: DateInput): string | undefined => {
  if (!dateValue) return undefined;

  if (typeof dateValue === 'string' && dateValue.includes('T')) {
    const [datePart] = dateValue.split('T');
    return convertDateToCivilString(datePart);
  }

  return convertDateToCivilString(dateValue);
};

const mapUserPosition = (
  person: UserWithPositions,
  context: UserPositionContext
): { memberId?: number; positionId?: number } => {
  const normalizedName = normalizeName(person.name);
  const user = context.users.find((u) => u.name && normalizeName(u.name) === normalizedName);
  const positionName = person.positions?.[0]?.name;
  let memberId: number | undefined;
  let positionId: number | undefined;
  const memberships = context.branchId
    ? user?.memberships?.filter((m) => m.branchId === context.branchId)
    : user?.memberships;
  if (memberships?.length && positionName?.trim()) {
    const normalizedPosition = normalizeName(positionName);
    const membership = memberships.find(
      (m) => m.position?.name && normalizeName(m.position.name) === normalizedPosition
    );
    memberId = membership?.id;
    positionId = membership?.position?.id;
  }
  if (memberId === undefined) {
    memberId = memberships?.[0]?.id;
  }
  if (positionId === undefined && positionName) {
    positionId = findPositionIdByName(positionName, context.positions);
  }
  if (!user && import.meta.env.DEV) {
    console.warn(`Usuário não encontrado: ${person.name}`);
  }
  return { memberId, positionId };
};

const populateElaboration = (
  formData: EditHomologationFormData,
  controlData: DocumentControlResponse | null,
  revision: DocumentRevision,
  context: UserPositionContext
): void => {
  if (controlData?.elaboration) {
    const { memberId, positionId } = mapUserPosition(controlData.elaboration.elaborator, context);
    formData.elaborationEditedByUserId = memberId;
    formData.elaborationEditedByPositionId = positionId;
    formData.elaborationPositionName = controlData.elaboration.elaborator.positions?.[0]?.name;
    formData.elaborationUserName = controlData.elaboration.elaborator.name;
    formData.elaborationCreatedAt = convertDateToISO(controlData.elaboration.elaboratedAt);
  } else if (revision.document?.createdBy) {
    formData.elaborationCreatedAt = revision.createdAt;
  }
};

const populateApproval = (
  formData: EditHomologationFormData,
  controlData: DocumentControlResponse | null,
  revision: DocumentRevision,
  context: UserPositionContext
): void => {
  if (controlData?.approval) {
    const { memberId, positionId } = mapUserPosition(controlData.approval.approver, context);
    formData.approvalUserId = controlData.approval.memberId ?? memberId;
    formData.approvalPositionId = controlData.approval.positionId ?? positionId;
    formData.approvalPositionName = controlData.approval.approver.positions?.[0]?.name;
    formData.approvalUserName = controlData.approval.approver.name;
    formData.approvalCreatedAt = convertDateToISO(controlData.approval.approvedAt);
  } else if (revision.approval) {
    formData.approvalCreatedAt = revision.approval.approvedAt || revision.approvedAt;
  }
};

export function resolveTrainingBranchIdForHomologation(
  documentBranchId: number | null | undefined,
  selectedBranchIdStr: string | null
): number | undefined {
  if (documentBranchId != null) {
    const n = Number(documentBranchId);
    if (!Number.isNaN(n) && n > 0) {
      return n;
    }
  }
  if (selectedBranchIdStr != null && selectedBranchIdStr !== '') {
    const n = Number(selectedBranchIdStr);
    if (!Number.isNaN(n) && n > 0) {
      return n;
    }
  }
  return undefined;
}

export function resolveTrainingMemberIdForUser(
  user: CollaboratorLike | undefined,
  branchId: number | undefined,
  trainingPositionIds: number[]
): number | undefined {
  if (!user || branchId === undefined) {
    return undefined;
  }

  const collected: MembershipLike[] = [];
  const seen = new Set<number>();
  const push = (m: MembershipLike | null | undefined) => {
    if (m?.id == null || seen.has(m.id)) {
      return;
    }
    seen.add(m.id);
    collected.push(m);
  };

  user.memberships?.forEach((m) => push(m as MembershipLike));
  if (user.membership) {
    push(user.membership as MembershipLike);
  }

  const branchMatches = (m: MembershipLike) => {
    const b = m.branchId ?? m.branch?.id;
    if (b === undefined) {
      return false;
    }
    return Number(b) === Number(branchId);
  };

  const inBranch = collected.filter(branchMatches);
  if (inBranch.length === 0) {
    return undefined;
  }

  const membershipPositionIds = (m: MembershipLike): Set<number> => {
    const ids = new Set<number>();
    if (m.positionId != null) {
      ids.add(m.positionId);
    }
    if (m.position?.id != null) {
      ids.add(m.position.id);
    }
    m.positions?.forEach((p) => {
      if (p.id != null) {
        ids.add(p.id);
      }
    });
    return ids;
  };

  if (trainingPositionIds.length > 0) {
    const wanted = new Set(trainingPositionIds);
    const withRole = inBranch.find((m) => {
      const mp = membershipPositionIds(m);
      for (const pid of wanted) {
        if (mp.has(pid)) {
          return true;
        }
      }
      return false;
    });
    if (withRole) {
      return withRole.id;
    }
  }

  return inBranch[0]?.id;
}

export function resolveTrainingParticipantUserIdStrings(
  trainingMemberIds: number[] | undefined,
  users: CollaboratorLike[],
  branchId: number | undefined
): string[] {
  if (!trainingMemberIds?.length) {
    return [];
  }
  return trainingMemberIds
    .map((memberId) => {
      const user = users.find((u) => {
        const inPlural = u.memberships?.some(
          (m) =>
            m.id === memberId && (branchId === undefined || Number(m.branchId) === Number(branchId))
        );
        if (inPlural) {
          return true;
        }
        const m = u.membership;
        if (!m || m.id !== memberId) {
          return false;
        }
        const bid = m.branchId ?? m.branch?.id;
        return branchId === undefined || (bid !== undefined && Number(bid) === Number(branchId));
      });
      return user?.userId ?? user?.id;
    })
    .filter((id): id is number => id != null)
    .map((id) => String(id));
}

export function buildBranchUserLookup(
  users: CollaboratorLike[],
  branchId: number | undefined
): UserLookup {
  const byId = new Map<number, CollaboratorLike>();

  for (const user of users) {
    if (user.id == null) {
      continue;
    }

    if (branchId !== undefined && collectBranchMemberships(user, branchId).length === 0) {
      continue;
    }

    byId.set(user.id, user);
  }

  return byId;
}

export function getUserPositionsFromLookup(
  userId: number | string,
  userLookup: UserLookup,
  branchId: number | undefined
): Array<{ id: number; name: string }> {
  const numId = typeof userId === 'string' ? Number.parseInt(userId, 10) : userId;
  if (!numId || Number.isNaN(numId)) {
    return [];
  }

  const user = userLookup.get(numId);
  if (!user) {
    return [];
  }

  const positions = collectBranchMemberships(user, branchId).flatMap((membership) => {
    const membershipPositions =
      membership.positions ?? (membership.position ? [membership.position] : []);
    return mapNamedPositions(membershipPositions);
  });

  return deduplicatePositions(positions);
}

const populateTrainingFromControl = (
  formData: EditHomologationFormData,
  training: DocumentControlTrainingData
): void => {
  formData.hasTraining = true;
  formData.trainingNotes = training.notes || '';

  formData.trainingPositionIds = training.positions?.map((p) => p.id);
  formData.trainingMemberIds = training.members?.map((m) => m.memberId);

  if (training.sessions?.length) {
    const memberByMemberId = new Map(training.members?.map((m) => [m.memberId, m]) ?? []);
    formData.trainingSessionUpdates = training.sessions.map((s) => {
      let responsibleUserPositionId = s.responsibleUserPositionId ?? undefined;
      if (responsibleUserPositionId === undefined && s.responsibleMemberId != null) {
        const sessionMember = memberByMemberId.get(s.responsibleMemberId);
        const firstPositionId = sessionMember?.user?.memberships?.[0]?.position?.id;
        if (firstPositionId != null) {
          responsibleUserPositionId = firstPositionId;
        }
      }
      return {
        sessionId: s.id,
        trainingId: training.trainingId ?? 0,
        trainingDate: normalizeTrainingDate(s.trainingDate) ?? '',
        responsibleMemberId: s.responsibleMemberId ?? undefined,
        responsibleUserPositionId,
      };
    });
  }
};

const populateTrainingFromRevision = (
  formData: EditHomologationFormData,
  revision: DocumentRevision
): void => {
  formData.hasTraining = true;
  formData.trainingNotes = revision.trainingDetails?.notes || '';
  formData.trainingPositionIds = revision.positions?.map((p) => p.id);
  formData.trainingMemberIds = revision.trainingDetails?.members?.map((m) => m.memberId);

  if (revision.trainingDetails?.sessions?.length) {
    const members = revision.trainingDetails.members ?? [];
    const memberByUserId = new Map(members.map((m) => [m.user.id, m]));
    formData.trainingSessionUpdates = revision.trainingDetails.sessions.map(
      (s): TrainingSessionUpdate => {
        const member =
          s.responsibleUserId != null ? memberByUserId.get(s.responsibleUserId) : undefined;
        return {
          sessionId: s.id,
          trainingId: s.trainingId,
          trainingDate: normalizeTrainingDate(s.trainingDate) ?? '',
          responsibleMemberId: s.responsibleMemberId ?? member?.memberId ?? undefined,
          responsibleUserPositionId:
            s.responsibleUserPositionId ?? member?.position?.id ?? undefined,
        };
      }
    );
  }
};

const populateTraining = (
  formData: EditHomologationFormData,
  controlData: DocumentControlResponse | null,
  revision: DocumentRevision
): void => {
  if (controlData?.training?.hasTraining) {
    populateTrainingFromControl(formData, controlData.training);
  } else if (revision.hasTraining && revision.trainingDetails) {
    populateTrainingFromRevision(formData, revision);
  }
};

const populateVerification = (
  formData: EditHomologationFormData,
  controlData: DocumentControlResponse | null,
  context: UserPositionContext
): void => {
  if (!controlData?.verification) return;

  const { memberId, positionId } = mapUserPosition(controlData.verification.verifier, context);
  formData.verificationUserId = controlData.verification.memberId ?? memberId;
  formData.verificationPositionId = controlData.verification.positionId ?? positionId;
  formData.verificationPositionName = controlData.verification.verifier.positions?.[0]?.name;
  formData.verificationUserName = controlData.verification.verifier.name;
  formData.verificationCreatedAt = convertDateToISO(controlData.verification.verifiedAt);
};

const populateEffectiveDate = (
  formData: EditHomologationFormData,
  controlData: DocumentControlResponse | null,
  revision: DocumentRevision
): void => {
  const dateSource = controlData?.effectiveDate ?? revision.effectiveDate;
  formData.effectiveDate = convertDateToCivilString(dateSource);
};

export function useEditHomologation() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const fetchUsersAndPositions = async (
    revision: DocumentRevision,
    users: CollaboratorLike[],
    positions: Position[]
  ): Promise<UserPositionContext> => {
    if (users.length > 0 && positions.length > 0) {
      return { users, positions };
    }

    const branchId = resolveHomologationBranchId(revision);

    try {
      const [memberUsers, positionsResponse] = await Promise.all([
        fetchAllMembersAsUsers(branchId),
        positionApi.getAllPositions({
          itemsPerPage: 10000,
          ...(branchId !== undefined ? { branchId } : {}),
        }),
      ]);
      return {
        users: memberUsers,
        positions: positionsResponse.data || [],
      };
    } catch (err) {
      console.error('Erro ao buscar usuários e posições:', err);
      return { users, positions };
    }
  };

  const populateFormFromRevision = async (
    revision: DocumentRevision,
    users: CollaboratorLike[] = [],
    positions: Position[] = []
  ): Promise<EditHomologationFormData> => {
    let controlData: DocumentControlResponse | null = null;

    try {
      controlData = await getDocumentControl(revision.id);
    } catch (err) {
      console.error('Erro ao buscar dados de controle:', err);
    }

    const baseContext = await fetchUsersAndPositions(revision, users, positions);
    const context: UserPositionContext = {
      ...baseContext,
      branchId: resolveHomologationBranchId(revision),
    };
    const formData: EditHomologationFormData = { hasTraining: revision.hasTraining ?? false };

    populateElaboration(formData, controlData, revision, context);
    populateApproval(formData, controlData, revision, context);
    populateTraining(formData, controlData, revision);
    populateVerification(formData, controlData, context);
    populateEffectiveDate(formData, controlData, revision);

    // The single "Observações/Justifique" field shows the training `notes` when
    // there is training, and the approval `justification` when there is not.
    formData.observations = formData.hasTraining
      ? (formData.trainingNotes ?? '')
      : (controlData?.approval?.justification ?? '');

    if (controlData?.previousRevisionEffectiveDate) {
      formData.previousRevisionEffectiveDate =
        convertDateToCivilString(controlData.previousRevisionEffectiveDate) ??
        controlData.previousRevisionEffectiveDate;
    }
    if (controlData?.verificationDate) {
      formData.currentVerificationDate =
        convertDateToISO(controlData.verificationDate) ?? controlData.verificationDate;
    }

    return formData;
  };

  const saveHomologation = async (
    revisionId: number,
    formData: EditHomologationFormData
  ): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      await editHomologationApi.updateHomologation(revisionId, formData);
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err);
      error.value = errorMessage;
      throw new Error(errorMessage);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    populateFormFromRevision,
    saveHomologation,
  };
}
